import { useEffect, useMemo, useState } from "react";
import useGridRowSelection from "hooks/useGridRowSelection";
import workflowService from "api/workflowService";
import { createPortal } from "react-dom";
import {
  DataGridPremium,
  GridColDef,
  GridRenderCellParams,
} from "@mui/x-data-grid-premium";
import { plGridLocales } from "utils/muiGridLocales";
import AddIcon from "assets/icons/add.svg?react";
import DeleteIcon from "assets/icons/delete_24.svg?react";
import { CatalogDoc, DocSource } from "../documentsStore";
import styles from "./styles.module.scss";

// Formularze aplikacji służące do rejestrowania nowych dokumentów
// (źródło dokumentu „Z formularza").
const APP_FORMS = [
  "Zaangażowanie",
  "Faktura zakupowa",
  "Faktura sprzedaży",
  "Rachunek",
  "Umowa",
];

// Podpowiedzi typu formularza (definiowalne — można wpisać własny).
const FORM_TYPE_SUGGESTIONS = [
  "Kadrowy",
  "Finansowy",
  "Prawny",
  "Administracyjny",
  "Sprzedażowy",
  "Zakupowy",
];

const FILE_FORMATS = ["DOCX", "PDF", "XLSX", "ODT"];

const sourceLabel = (d: CatalogDoc) =>
  d.source === "form" ? "Formularz" : `Plik: ${d.fileFormat ?? ""}`;

const fmtDate = (iso: string) =>
  new Date(iso).toLocaleDateString("pl-PL", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });

type Filter = "all" | DocSource;

const DocumentsCatalog = () => {
  const { selectedRowIds, rowsRef, handleRowClick } = useGridRowSelection();
  // Trwałość katalogu w BE (per użytkownik + aktywny profil). Lokalny stan jest
  // hydratowany z serwera i odzwierciedla optymistycznie zmiany przed dosyncowaniem.
  const { data: serverDocs } = workflowService.useGetDocuments();
  const syncDocs = workflowService.useSyncDocuments();
  const [docs, setDocs] = useState<CatalogDoc[]>([]);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState<Filter>("all");
  const [showAdd, setShowAdd] = useState(false);

  useEffect(() => {
    if (serverDocs) setDocs(serverDocs);
  }, [serverDocs]);

  const persist = (next: CatalogDoc[]) => {
    setDocs(next);
    syncDocs.mutate(next);
  };

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    return docs.filter((d) => {
      if (filter !== "all" && d.source !== filter) return false;
      if (!q) return true;
      return [d.name, sourceLabel(d), d.description]
        .filter(Boolean)
        .some((v) => (v as string).toLowerCase().includes(q));
    });
  }, [docs, search, filter]);
  rowsRef.current = visible;

  const removeDoc = (id: string) => persist(docs.filter((d) => d.id !== id));
  const toggleActive = (id: string) =>
    persist(
      docs.map((d) => (d.id === id ? { ...d, isActive: !d.isActive } : d)),
    );

  const addDoc = (doc: CatalogDoc) => {
    persist([...docs, doc]);
    setShowAdd(false);
  };

  const columns: GridColDef[] = [
    {
      field: "name",
      headerName: "Nazwa",
      flex: 1,
      minWidth: 200,
      renderCell: (p: GridRenderCellParams<CatalogDoc>) => (
        <div className={styles.nameCell}>
          <span className={styles.nameText}>{p.row.name}</span>
          {p.row.description && (
            <span className={styles.desc}>{p.row.description}</span>
          )}
          <span className={styles.workflow}>
            Workflow: {p.row.workflowTitle || "brak powiązania"}
          </span>
        </div>
      ),
    },
    {
      field: "source",
      headerName: "Źródło",
      width: 210,
      valueGetter: (_, row: CatalogDoc) => sourceLabel(row),
      renderCell: (p: GridRenderCellParams<CatalogDoc>) => (
        <span
          className={`${styles.srcBadge} ${
            p.row.source === "form" ? styles.srcForm : styles.srcEditor
          }`}
        >
          {sourceLabel(p.row)}
        </span>
      ),
    },
    {
      field: "isActive",
      headerName: "Status",
      width: 130,
      valueGetter: (_, row: CatalogDoc) => (row.isActive ? "Aktywny" : "Nieaktywny"),
      renderCell: (p: GridRenderCellParams<CatalogDoc>) => (
        <span
          className={`${styles.status} ${
            p.row.isActive ? styles.active : styles.inactive
          }`}
          onClick={() => toggleActive(p.row.id)}
          title="Kliknij, aby zmienić status"
        >
          {p.row.isActive ? "Aktywny" : "Nieaktywny"}
        </span>
      ),
    },
    {
      field: "createdAt",
      headerName: "Dodano",
      width: 120,
      valueFormatter: (v) => fmtDate(v as string),
    },
    {
      field: "actions",
      headerName: "",
      width: 64,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      align: "center",
      renderCell: (p: GridRenderCellParams<CatalogDoc>) => (
        <button
          className={styles.iconBtn}
          title="Usuń"
          onClick={() => removeDoc(p.row.id)}
        >
          <DeleteIcon style={{ width: 16, height: 16 }} />
        </button>
      ),
    },
  ];

  return (
    <div className={styles.wrapper}>
      <div className={styles.intro}>
        Dokumenty podlegające akceptacji w procedurze workflow. Lista obejmuje
        zarówno dokumenty plikowe (z edytora, np. Word), jak i dokumenty
        tworzone na podstawie danych z dedykowanych formularzy (np. zaangażowanie).
      </div>

      <div className={styles.toolbar}>
        <input
          className={styles.search}
          placeholder="Szukaj dokumentu…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <select
          className={styles.filterSelect}
          value={filter}
          onChange={(e) => setFilter(e.target.value as Filter)}
        >
          <option value="all">Wszystkie</option>
          <option value="editor">Z edytora</option>
          <option value="form">Z formularza</option>
        </select>
        <button className={styles.addBtn} onClick={() => setShowAdd(true)}>
          <AddIcon style={{ width: 14, height: 14 }} /> Dodaj dokument
        </button>
      </div>

      <div className={styles.gridWrap}>
        <DataGridPremium
          rows={visible}
          columns={columns}
          localeText={plGridLocales}
          columnHeaderHeight={36}
          rowHeight={64}
          hideFooter
          disableColumnFilter
          disablePivoting
          disableAggregation
          disableRowSelectionOnClick
          rowSelectionModel={selectedRowIds}
          onRowClick={handleRowClick}
          sx={{
            border: 0,
            background: "#fff",
            borderRadius: "10px",
            "& .MuiDataGrid-cell:focus-within, & .MuiDataGrid-cell:focus": {
              outline: "none !important",
            },
            "& .MuiDataGrid-columnHeader:focus-within, & .MuiDataGrid-columnHeader:focus":
              { outline: "none !important" },
          }}
        />
      </div>

      {showAdd &&
        createPortal(
          <AddDocumentSlide
            onClose={() => setShowAdd(false)}
            onSave={addDoc}
          />,
          document.body,
        )}
    </div>
  );
};

// ── Slide „Dodaj dokument" ────────────────────────────────────────────────

interface AddProps {
  onClose: () => void;
  onSave: (doc: CatalogDoc) => void;
}

const AddDocumentSlide = ({ onClose, onSave }: AddProps) => {
  const [source, setSource] = useState<DocSource>("form");
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [formType, setFormType] = useState("");
  const [fileFormat, setFileFormat] = useState(FILE_FORMATS[0]);

  const canSave = name.trim().length > 0;

  const save = () => {
    if (!canSave) return;
    onSave({
      id: `doc-${Date.now()}`,
      name: name.trim(),
      description: description.trim() || undefined,
      source,
      formType: source === "form" ? formType.trim() || undefined : undefined,
      fileFormat: source === "editor" ? fileFormat : undefined,
      isActive: true,
      createdAt: new Date().toISOString(),
    });
  };

  return (
    <div
      className={styles.slideOverlay}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className={styles.slidePanel}>
        <div className={styles.slideHeader}>
          <span className={styles.slideTitle}>Dodaj dokument</span>
          <button className={styles.slideClose} onClick={onClose} title="Zamknij">
            ✕
          </button>
        </div>
        <div className={styles.slideBody}>
          <div className={styles.fieldLabel}>Źródło dokumentu</div>
          <div className={styles.sourcePicker}>
            <button
              className={`${styles.sourceCard} ${
                source === "form" ? styles.sourceActive : ""
              }`}
              onClick={() => setSource("form")}
            >
              <div className={styles.sourceCardTitle}>Z formularza</div>
              <div className={styles.sourceCardDesc}>
                Dokument z danych wpisanych do dedykowanego formularza (np.
                zarejestrowana umowa).
              </div>
            </button>
            <button
              className={`${styles.sourceCard} ${
                source === "editor" ? styles.sourceActive : ""
              }`}
              onClick={() => setSource("editor")}
            >
              <div className={styles.sourceCardTitle}>Z edytora (plik)</div>
              <div className={styles.sourceCardDesc}>
                Dokument plikowy przygotowany w edytorze (np. Word / PDF).
              </div>
            </button>
          </div>

          {source === "form" ? (
            <>
              <div className={styles.fieldLabel}>Typ formularza</div>
              <input
                className={styles.input}
                list="doc-form-type-suggestions"
                placeholder="np. Kadrowy"
                value={formType}
                onChange={(e) => setFormType(e.target.value)}
              />
              <datalist id="doc-form-type-suggestions">
                {FORM_TYPE_SUGGESTIONS.map((s) => (
                  <option key={s} value={s} />
                ))}
              </datalist>
            </>
          ) : (
            <>
              <div className={styles.fieldLabel}>Format pliku</div>
              <select
                className={styles.input}
                value={fileFormat}
                onChange={(e) => setFileFormat(e.target.value)}
              >
                {FILE_FORMATS.map((f) => (
                  <option key={f} value={f}>
                    {f}
                  </option>
                ))}
              </select>
            </>
          )}

          <div className={styles.fieldLabel}>Nazwa dokumentu</div>
          {source === "form" ? (
            <select
              className={styles.input}
              value={name}
              onChange={(e) => setName(e.target.value)}
            >
              <option value="">— wybierz formularz —</option>
              {APP_FORMS.map((f) => (
                <option key={f} value={f}>
                  {f}
                </option>
              ))}
            </select>
          ) : (
            <input
              className={styles.input}
              placeholder="np. Pismo procesowe"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          )}

          <div className={styles.fieldLabel}>Opis (opcjonalnie)</div>
          <textarea
            className={`${styles.input} ${styles.textarea}`}
            rows={3}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />

          <div className={styles.slideActions}>
            <button
              className={styles.saveBtn}
              disabled={!canSave}
              onClick={save}
            >
              Dodaj do listy
            </button>
            <button className={styles.cancelBtn} onClick={onClose}>
              Anuluj
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DocumentsCatalog;
